import { ReactComponent as AcknowledgedIcon } from '@elementary/common-ui/assets/20x20/incident-acknowledged.svg';
import { ReactComponent as OpenIcon } from '@elementary/common-ui/assets/20x20/incident-open.svg';
import { ReactComponent as ResolvedIcon } from '@elementary/common-ui/assets/20x20/incident-resolved.svg';
import { ReactComponent as SeverityCriticalIcon } from '@elementary/common-ui/assets/severity_critical.svg';
import { ReactComponent as SeverityCriticalGrayscaleIcon } from '@elementary/common-ui/assets/severity_critical_grayscale.svg';
import { ReactComponent as SeverityHighIcon } from '@elementary/common-ui/assets/severity_high.svg';
import { ReactComponent as SeverityHighGrayscaleIcon } from '@elementary/common-ui/assets/severity_high_grayscale.svg';
import { ReactComponent as SeverityLowIcon } from '@elementary/common-ui/assets/severity_low.svg';
import { ReactComponent as SeverityLowGrayscaleIcon } from '@elementary/common-ui/assets/severity_low_grayscale.svg';
import { ReactComponent as SeverityNormalIcon } from '@elementary/common-ui/assets/severity_normal.svg';
import { ReactComponent as SeverityNormalGrayscaleIcon } from '@elementary/common-ui/assets/severity_normal_grayscale.svg';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';
import { GROUPING_STRATEGY_DISPLAY } from '../constants';
import type { Severity, Status } from '../types';

export const severityIconMap: Record<Severity, JSX.Element> = {
  Low: <SeverityLowIcon />,
  Normal: <SeverityNormalIcon />,
  High: <SeverityHighIcon />,
  Critical: <SeverityCriticalIcon />,
};

export const severityGrayscaleIconMap: Record<Severity, JSX.Element> = {
  Low: <SeverityLowGrayscaleIcon />,
  Normal: <SeverityNormalGrayscaleIcon />,
  High: <SeverityHighGrayscaleIcon />,
  Critical: <SeverityCriticalGrayscaleIcon />,
};

export const statusIconMap: Record<Status, JSX.Element> = {
  Open: <OpenIcon width={20} />,
  Acknowledged: <AcknowledgedIcon width={20} />,
  Resolved: <ResolvedIcon width={20} />,
};

export const smallStatusIconMap: Record<Status, JSX.Element> = {
  Open: <OpenIcon width={18} />,
  Acknowledged: <AcknowledgedIcon width={18} />,
  Resolved: <ResolvedIcon width={18} />,
};

const SIMPLE_SUB_TYPES = ['generic', 'dimension', 'singular'];

export const getIncidentDisplayLabel = (
  displayName: string,
  subType?: string,
): string =>
  !subType || SIMPLE_SUB_TYPES.includes(subType)
    ? displayName
    : `${displayName} - ${subType}`;

export const getGroupingStrategyDisplay = (
  value: string | undefined,
): string => {
  const normalized = value ?? '';
  const key = normalized.toLowerCase();
  if (Object.hasOwn(GROUPING_STRATEGY_DISPLAY, key)) {
    return GROUPING_STRATEGY_DISPLAY[key];
  }
  return normalized
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getAlertDestinationLabel = (
  integrationType?: AlertIntegrationType,
): string => {
  switch (integrationType) {
    case 'SLACK':
      return ' via Slack';
    case 'MICROSOFT_TEAMS':
      return ' via Microsoft Teams';
    case 'PAGER_DUTY':
      return ' via PagerDuty';
    case 'OPSGENIE':
      return ' via Opsgenie';
    case 'INCIDENT_IO':
      return ' via incident.io';
    case 'ROOTLY':
      return ' via Rootly';
    case 'EMAIL':
      return ' via Email';
    default:
      return '';
  }
};

export const formatAlertDestinationChannel = (destination?: {
  type?: AlertIntegrationType;
  channel?: string;
}): string => {
  const channel = destination?.channel;
  if (!channel) return '';
  if (destination?.type === 'EMAIL') {
    try {
      const parsed = JSON.parse(channel) as { to?: unknown };
      const to = Array.isArray(parsed.to)
        ? parsed.to
            .filter((v): v is string => typeof v === 'string')
            .map((v) => v.trim())
            .filter((v) => v.length > 0)
        : [];
      if (to.length === 0) return '';
      if (to.length <= 2) return ` to ${to.join(', ')}`;
      return ` to ${to[0]} +${to.length - 1} more`;
    } catch {
      return '';
    }
  }
  if (destination?.type === 'SLACK') return ` to #${channel}`;
  return ` to ${channel}`;
};

export const toSafeSlackUrl = (value?: string): string | undefined => {
  if (!value) return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:') return undefined;
    return parsed.toString();
  } catch {
    return undefined;
  }
};
